import React from "react";

/**
 * Shared card primitives for the 4-pillar redesign. Keeps every card on
 * every pillar visually consistent without polluting each view file.
 *
 * Design constraints (match rest of app):
 *   • Dark black background · brand-gradient (green→cyan) or accent
 *     borders · font-mono for labels · font-display for values
 *   • Value ranges use color semantics:
 *       #39FF14 = healthy / positive
 *       #00E5FF = neutral info
 *       #FFB020 = watch / warm
 *       #FF6B57 = risk / hot
 */

const COLOR = {
  green:  "#39FF14",
  cyan:   "#00E5FF",
  amber:  "#FFB020",
  red:    "#FF6B57",
  blue:   "#7FD1FF",
  zinc:   "rgba(255,255,255,0.14)",
};

/**
 * PillarSectionHeader — the "PILLAR · NAME" band above each view.
 */
export const PillarSectionHeader = ({ eyebrow, title, kicker, testid }) => (
  <div className="pt-1 pb-1" data-testid={testid}>
    <div className="font-mono text-[9px] uppercase tracking-[0.22em] text-zinc-500">{eyebrow}</div>
    <h2 className="font-display text-xl text-white font-semibold leading-tight mt-0.5">{title}</h2>
    {kicker && <p className="text-[12px] text-zinc-400 leading-relaxed mt-1">{kicker}</p>}
  </div>
);

/**
 * StatBigCard — one hero-sized stat, used for headline pillar KPIs.
 */
export const StatBigCard = ({
  label,
  value,
  unit,
  trend,
  color = COLOR.green,
  live,
  testid,
}) => (
  <div
    className="rounded-xl border p-4"
    style={{ borderColor: COLOR.zinc, background: "rgba(255,255,255,0.02)" }}
    data-testid={testid}
  >
    <div className="flex items-center justify-between mb-1">
      <span className="font-mono text-[9px] uppercase tracking-wider text-zinc-500">{label}</span>
      {live !== undefined && (
        <span
          className="inline-flex items-center gap-1 font-mono text-[9px] uppercase tracking-wider"
          style={{ color: live ? color : "rgba(255,255,255,0.35)" }}
        >
          <span
            className="w-1 h-1 rounded-full"
            style={{ background: live ? color : "rgba(255,255,255,0.35)" }}
          />
          {live ? "live" : "waiting"}
        </span>
      )}
    </div>
    <div className="flex items-baseline gap-1">
      <span className="font-display text-3xl text-white font-semibold leading-none">{value}</span>
      {unit && <span className="font-mono text-[11px] text-zinc-400">{unit}</span>}
    </div>
    {trend && (
      <div className="font-mono text-[10px] mt-1.5" style={{ color }}>{trend}</div>
    )}
  </div>
);

/**
 * StatChipCard — compact 2-up card. Same visual footprint as the
 * existing HomeView StatCard so a grid mixes cleanly.
 */
export const StatChipCard = ({ label, value, trend, color = COLOR.green, testid }) => (
  <div
    className="rounded-xl border p-3"
    style={{ borderColor: COLOR.zinc, background: "rgba(255,255,255,0.02)" }}
    data-testid={testid}
  >
    <div className="font-mono text-[9px] uppercase tracking-wider text-zinc-500 mb-1">
      {label}
    </div>
    <div className="font-display text-xl text-white font-semibold leading-none">{value}</div>
    {trend && (
      <div className="font-mono text-[10px] mt-1" style={{ color }}>{trend}</div>
    )}
  </div>
);

/**
 * MeterBarCard — 0-100 metric with a horizontal fill bar. Great for
 * Thermal Stress, Charging Quality, Calibration Confidence.
 */
export const MeterBarCard = ({
  label,
  value,        // 0..100
  suffix = "",
  desc,
  color = COLOR.green,
  inverse = false, // when true, higher value = worse
  testid,
}) => {
  const pct = Math.max(0, Math.min(100, value));
  const barColor = inverse
    ? pct > 60 ? COLOR.red : pct > 30 ? COLOR.amber : COLOR.green
    : pct >= 70 ? COLOR.green : pct >= 45 ? COLOR.amber : COLOR.red;

  return (
    <div
      className="rounded-xl border p-4"
      style={{ borderColor: COLOR.zinc, background: "rgba(255,255,255,0.02)" }}
      data-testid={testid}
    >
      <div className="flex items-baseline justify-between mb-1.5">
        <span className="font-mono text-[10px] uppercase tracking-wider text-zinc-400">
          {label}
        </span>
        <span className="font-display text-lg text-white font-semibold leading-none">
          {value}
          <span className="font-mono text-[10px] text-zinc-500 ml-0.5">{suffix}</span>
        </span>
      </div>
      <div className="h-1.5 rounded-full overflow-hidden bg-white/5">
        <div
          className="h-full rounded-full transition-all"
          style={{
            width: `${pct}%`,
            background: `linear-gradient(90deg, ${barColor}88, ${barColor})`,
            boxShadow: `0 0 12px ${barColor}55`,
          }}
        />
      </div>
      {desc && (
        <div className="text-[11px] text-zinc-400 leading-relaxed mt-2">{desc}</div>
      )}
    </div>
  );
};

/**
 * FactorListCard — used for Risk Score contributing factors, service
 * timelines, etc. Renders a list of {icon, text, tone} rows.
 */
export const FactorListCard = ({
  header,
  items = [],
  testid,
}) => (
  <div
    className="rounded-xl border p-4 space-y-2.5"
    style={{ borderColor: COLOR.zinc, background: "rgba(255,255,255,0.02)" }}
    data-testid={testid}
  >
    {header && (
      <div className="font-mono text-[10px] uppercase tracking-wider text-zinc-500 pb-1">
        {header}
      </div>
    )}
    {items.map((it, i) => {
      const Icon = it.icon;
      return (
        <div key={i} className="flex items-start gap-2.5">
          {Icon && (
            <div
              className="w-6 h-6 rounded-md flex items-center justify-center shrink-0 border"
              style={{
                background: `${it.color || COLOR.cyan}12`,
                borderColor: `${it.color || COLOR.cyan}44`,
              }}
            >
              <Icon
                className="w-3 h-3"
                style={{ color: it.color || COLOR.cyan }}
                strokeWidth={2.4}
              />
            </div>
          )}
          <div className="min-w-0 flex-1">
            <div className="text-[12px] text-white leading-tight">{it.text}</div>
            {it.hint && (
              <div className="font-mono text-[10px] text-zinc-500 leading-tight mt-0.5">
                {it.hint}
              </div>
            )}
          </div>
          {it.right && (
            <span
              className="font-mono text-[10px] text-white/80 shrink-0"
              style={{ color: it.rightColor || undefined }}
            >
              {it.right}
            </span>
          )}
        </div>
      );
    })}
  </div>
);

export { COLOR };
